import { ENUM_LABELS, type ReagentRecipeRow } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { RESOURCE } from '#client/lib/routes';
import { formatBaseQuantity } from '#client/lib/format';

const columns: Column<ReagentRecipeRow>[] = [
  {
    key: 'target',
    header: 'Consumed by',
    frozen: true,
    render: (row) => (
      <span>
        <strong>{row.test_name ?? row.parameter_name}</strong>
        {/* Which level a line sits at decides whether it is drawn once per
            sample or once per analyte, so it is not a detail worth hiding. */}
        <span className="c-inventory__code">
          {row.test_name === null ? 'per analyte' : 'per sample'}
        </span>
      </span>
    ),
  },
  {
    key: 'item',
    header: 'Reagent',
    render: (row) => (
      <span>
        {row.item_name}
        <span className="c-inventory__code ident">{row.item_code}</span>
      </span>
    ),
  },
  {
    key: 'quantity',
    header: 'Per run',
    width: '150px',
    align: 'end',
    render: (row) => (
      <span className="tnum">
        {formatBaseQuantity(row.quantity_base)} {ENUM_LABELS.stockUnit[row.base_unit]}
      </span>
    ),
  },
  {
    key: 'note',
    header: 'From the SOP',
    render: (row) => row.note ?? <span className="text-fg-muted">—</span>,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '110px',
    render: (row) =>
      row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Off</Badge>,
  },
];

/**
 * The consumption recipes — the lab's SOP documents, made executable.
 *
 * "ALP (Centronics): Working Reagent 400 µL R1 + 100 µL R2" is two rows here,
 * both attached to the ALP *parameter*. A shared diluent a whole sample draws
 * once attaches to the *test* instead. Where a line sits is the difference
 * between charging a reagent once per run and once per analyte, which is why
 * the list shows it.
 */
export function RecipeListPage() {
  return (
    <ResourceListPage<ReagentRecipeRow>
      title="Consumption recipes"
      subtitle="How much of each reagent one run of a test consumes"
      path={RESOURCE.reagentRecipes}
      columns={columns}
      noun="recipe"
      nounPlural="recipes"
      searchPlaceholder="Search by test, parameter or reagent"
      rowLabel={(row) => `${row.test_name ?? row.parameter_name} — ${row.item_name}`}
      createCapability="recipe.create"
      editCapability="recipe.edit"
      deleteCapability="recipe.delete"
      emptyBody="Without a recipe a test deducts nothing, which the coverage report will tell you about."
    />
  );
}
